import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { DebounceEmitter, Emitter } from 'vscode/vscode/vs/base/common/event';
import 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { TernarySearchTree } from 'vscode/vscode/vs/base/common/ternarySearchTree';
import { DisposableStore, toDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { isThenable } from 'vscode/vscode/vs/base/common/async';
import { LinkedList } from 'vscode/vscode/vs/base/common/linkedList';
import { createCSSRule, asCSSPropertyValue, removeCSSRulesContainingSelector, createStyleSheet } from 'vscode/vscode/vs/base/browser/dom';
import { IThemeService } from 'vscode/vscode/vs/platform/theme/common/themeService.service';
import { ThemeIcon } from 'vscode/vscode/vs/base/common/themables';
import { isFalsyOrWhitespace } from 'vscode/vscode/vs/base/common/strings';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { isCancellationError } from 'vscode/vscode/vs/base/common/errors';
import { CancellationTokenSource } from 'vscode/vscode/vs/base/common/cancellation';
import 'vscode/vscode/vs/platform/instantiation/common/extensions';
import { hash } from 'vscode/vscode/vs/base/common/hash';
import { IUriIdentityService } from 'vscode/vscode/vs/platform/uriIdentity/common/uriIdentity.service';
import { distinct, asArray } from 'vscode/vscode/vs/base/common/arrays';
import { asCssVariable } from 'vscode/vscode/vs/platform/theme/common/colorUtils';
import 'vscode/vscode/vs/platform/theme/common/colors/baseColors';
import 'vscode/vscode/vs/platform/theme/common/colors/chartsColors';
import 'vscode/vscode/vs/platform/theme/common/colors/editorColors';
import 'vscode/vscode/vs/platform/theme/common/colors/inputColors';
import 'vscode/vscode/vs/platform/theme/common/colors/listColors';
import 'vscode/vscode/vs/platform/theme/common/colors/menuColors';
import 'vscode/vscode/vs/platform/theme/common/colors/minimapColors';
import 'vscode/vscode/vs/platform/theme/common/colors/miscColors';
import 'vscode/vscode/vs/platform/theme/common/colors/quickpickColors';
import 'vscode/vscode/vs/platform/theme/common/colors/searchColors';
import { getIconRegistry } from 'vscode/vscode/vs/platform/theme/common/iconRegistry';

const _moduleId = "vs/workbench/services/decorations/browser/decorationsService";
class DecorationRule {
    static keyOf(data) {
        if (Array.isArray(data)) {
            return ( (data.map(DecorationRule.keyOf))).join(',');
        }
        else {
            const { color, letter } = data;
            if (ThemeIcon.isThemeIcon(letter)) {
                return `${color}+${letter.id}`;
            }
            else {
                return `${color}/${letter}`;
            }
        }
    }
    static { this._classNamesPrefix = 'monaco-decoration'; }
    constructor(themeService, data, key) {
        this.themeService = themeService;
        this._refCounter = 0;
        this.data = data;
        const suffix = ( (hash(key).toString(36)));
        this.itemColorClassName = `${DecorationRule._classNamesPrefix}-itemColor-${suffix}`;
        this.itemBadgeClassName = `${DecorationRule._classNamesPrefix}-itemBadge-${suffix}`;
        this.bubbleBadgeClassName = `${DecorationRule._classNamesPrefix}-bubbleBadge-${suffix}`;
        this.iconBadgeClassName = `${DecorationRule._classNamesPrefix}-iconBadge-${suffix}`;
    }
    acquire() {
        this._refCounter += 1;
    }
    release() {
        return --this._refCounter === 0;
    }
    appendCSSRules(element) {
        if (!Array.isArray(this.data)) {
            this._appendForOne(this.data, element);
        }
        else {
            this._appendForMany(this.data, element);
        }
    }
    _appendForOne(data, element) {
        const { color, letter } = data;
        createCSSRule(`.${this.itemColorClassName}`, `color: ${getColor(color)};`, element);
        if (ThemeIcon.isThemeIcon(letter)) {
            this._createIconCSSRule(letter, color, element);
        }
        else if (letter) {
            createCSSRule(`.${this.itemBadgeClassName}::after`, `content: "${letter}"; color: ${getColor(color)};`, element);
        }
    }
    _appendForMany(data, element) {
        const { color } = data.find(d => !!d.color) ?? data[0];
        createCSSRule(`.${this.itemColorClassName}`, `color: ${getColor(color)};`, element);
        const letters = [];
        let icon;
        for (const d of data) {
            if (ThemeIcon.isThemeIcon(d.letter)) {
                icon = d.letter;
                break;
            }
            else if (d.letter) {
                letters.push(d.letter);
            }
        }
        if (icon) {
            this._createIconCSSRule(icon, color, element);
        }
        else {
            if (letters.length) {
                createCSSRule(`.${this.itemBadgeClassName}::after`, `content: "${letters.join(', ')}"; color: ${getColor(color)};`, element);
            }
            createCSSRule(`.${this.bubbleBadgeClassName}::after`, `content: "\uea71"; color: ${getColor(color)}; font-family: codicon; font-size: 14px; margin-right: 14px; opacity: 0.4;`, element);
        }
    }
    _createIconCSSRule(icon, color, element) {
        const modifier = ThemeIcon.getModifier(icon);
        if (modifier) {
            icon = ThemeIcon.modify(icon, undefined);
        }
        const iconContribution = getIconRegistry().getIcon(icon.id);
        if (!iconContribution) {
            return;
        }
        const definition = this.themeService.getProductIconTheme().getIcon(iconContribution);
        if (!definition) {
            return;
        }
        createCSSRule(`.${this.iconBadgeClassName}::after`, `content: '${definition.fontCharacter}';
			color: ${icon.color ? getColor(icon.color.id) : getColor(color)};
			font-family: ${asCSSPropertyValue(definition.font?.id ?? 'codicon')};
			font-size: 16px;
			margin-right: 14px;
			font-weight: normal;
			${modifier === 'spin' ? 'animation: codicon-spin 1.5s steps(30) infinite' : ''};
			`, element);
    }
    removeCSSRules(element) {
        removeCSSRulesContainingSelector(this.itemColorClassName, element);
        removeCSSRulesContainingSelector(this.itemBadgeClassName, element);
        removeCSSRulesContainingSelector(this.bubbleBadgeClassName, element);
        removeCSSRulesContainingSelector(this.iconBadgeClassName, element);
    }
}
class DecorationStyles {
    constructor(_themeService) {
        this._themeService = _themeService;
        this._dispoables = ( (new DisposableStore()));
        this._styleElement = createStyleSheet(undefined, undefined, this._dispoables);
        this._decorationRules = ( (new Map()));
    }
    dispose() {
        this._dispoables.dispose();
    }
    asDecoration(data, onlyChildren) {
        data.sort((a, b) => (b.weight || 0) - (a.weight || 0));
        const key = DecorationRule.keyOf(data);
        let rule = this._decorationRules.get(key);
        if (!rule) {
            rule = ( (new DecorationRule(this._themeService, data, key)));
            this._decorationRules.set(key, rule);
            rule.appendCSSRules(this._styleElement);
        }
        rule.acquire();
        const labelClassName = rule.itemColorClassName;
        let badgeClassName = rule.itemBadgeClassName;
        const iconClassName = rule.iconBadgeClassName;
        let tooltip = distinct(( (data.filter(d => !isFalsyOrWhitespace(d.tooltip)).map(d => d.tooltip)))).join(' • ');
        const strikethrough = ( (data.some(d => d.strikethrough)));
        if (onlyChildren) {
            badgeClassName = rule.bubbleBadgeClassName;
            tooltip = ( localizeWithPath(_moduleId, 0, "Contains emphasized items"));
        }
        return {
            labelClassName,
            badgeClassName,
            iconClassName,
            strikethrough,
            tooltip,
            dispose: () => {
                if (rule?.release()) {
                    this._decorationRules.delete(key);
                    rule.removeCSSRules(this._styleElement);
                    rule = undefined;
                }
            }
        };
    }
}
class FileDecorationChangeEvent {
    constructor(all) {
        this._data = TernarySearchTree.forUris(_uri => true);
        this._data.fill(true, asArray(all));
    }
    affectsResource(uri) {
        return this._data.hasElementOrSubtree(uri);
    }
}
class DecorationDataRequest {
    constructor(source, thenable) {
        this.source = source;
        this.thenable = thenable;
    }
}
function getColor(color) {
    return color ? asCssVariable(color) : 'inherit';
}
let DecorationsService = class DecorationsService {
    constructor(uriIdentityService, themeService) {
        this._onDidChangeDecorationsDelayed = ( (new DebounceEmitter({ merge: all => all.flat() })));
        this._onDidChangeDecorations = ( (new Emitter()));
        this.onDidChangeDecorations = this._onDidChangeDecorations.event;
        this._provider = ( (new LinkedList()));
        this._decorationStyles = ( (new DecorationStyles(themeService)));
        this._data = TernarySearchTree.forUris(key => uriIdentityService.extUri.ignorePathCasing(key));
        this._onDidChangeDecorationsDelayed.event(event => { this._onDidChangeDecorations.fire(( (new FileDecorationChangeEvent(event)))); });
    }
    dispose() {
        this._onDidChangeDecorations.dispose();
        this._onDidChangeDecorationsDelayed.dispose();
        this._data.clear();
    }
    registerDecorationsProvider(provider) {
        const rm = this._provider.unshift(provider);
        this._onDidChangeDecorations.fire({
            affectsResource() { return true; }
        });
        const removeAll = () => {
            const uris = [];
            for (const [uri, map] of this._data) {
                if (map.delete(provider)) {
                    uris.push(uri);
                }
            }
            if (uris.length > 0) {
                this._onDidChangeDecorationsDelayed.fire(uris);
            }
        };
        const listener = provider.onDidChange(uris => {
            if (!uris) {
                removeAll();
            }
            else {
                for (const uri of uris) {
                    const map = this._ensureEntry(uri);
                    this._fetchData(map, uri, provider);
                }
            }
        });
        return toDisposable(() => {
            rm();
            listener.dispose();
            removeAll();
        });
    }
    _ensureEntry(uri) {
        let map = this._data.get(uri);
        if (!map) {
            map = ( (new Map()));
            this._data.set(uri, map);
        }
        return map;
    }
    getDecoration(uri, includeChildren) {
        const all = [];
        let containsChildren = false;
        const map = this._ensureEntry(uri);
        for (const provider of this._provider) {
            let data = map.get(provider);
            if (data === undefined) {
                data = this._fetchData(map, uri, provider);
            }
            if (data && !(data instanceof DecorationDataRequest)) {
                all.push(data);
            }
        }
        if (includeChildren) {
            const iter = this._data.findSuperstr(uri);
            if (iter) {
                for (const tuple of iter) {
                    for (const data of ( (tuple[1].values()))) {
                        if (data && !(data instanceof DecorationDataRequest)) {
                            if (data.bubble) {
                                all.push(data);
                                containsChildren = true;
                            }
                        }
                    }
                }
            }
        }
        return all.length === 0
            ? undefined
            : this._decorationStyles.asDecoration(all, containsChildren);
    }
    _fetchData(map, uri, provider) {
        const pendingRequest = map.get(provider);
        if (pendingRequest instanceof DecorationDataRequest) {
            pendingRequest.source.cancel();
            map.delete(provider);
        }
        const cts = ( (new CancellationTokenSource()));
        const dataOrThenable = provider.provideDecorations(uri, cts.token);
        if (!isThenable(dataOrThenable)) {
            cts.dispose();
            return this._keepItem(map, provider, uri, dataOrThenable);
        }
        else {
            const request = ( (new DecorationDataRequest(cts, Promise.resolve(dataOrThenable).then(data => {
                if (map.get(provider) === request) {
                    this._keepItem(map, provider, uri, data);
                }
            }).catch(err => {
                if (!isCancellationError(err) && map.get(provider) === request) {
                    map.delete(provider);
                }
            }).finally(() => {
                cts.dispose();
            }))));
            map.set(provider, request);
            return null;
        }
    }
    _keepItem(map, provider, uri, data) {
        const deco = data ? data : null;
        const old = map.get(provider);
        map.set(provider, deco);
        if (deco || old) {
            this._onDidChangeDecorationsDelayed.fire(uri);
        }
        return deco;
    }
};
DecorationsService = ( (__decorate([
    ( (__param(0, IUriIdentityService))),
    ( (__param(1, IThemeService)))
], DecorationsService)));

export { DecorationsService };
